<template>
  <div class="flex min-h-full flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
    <div class="w-full max-w-lg space-y-10">
      <div>
        <img class="mx-auto h-30 w-auto" :src="logoUrl" alt="ManageRunners logo" />
        <h2
          class="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900 dark:text-white"
        >
          Connect an app
        </h2>
      </div>

      <div
        class="rounded-2xl border border-gray-200 bg-white p-6 dark:border-white/10 dark:bg-gray-900/60 shadow-xs"
        data-testid="oauth-consent"
        :data-state="state"
      >
        <div
          v-if="state === 'loading'"
          class="text-sm text-gray-500 dark:text-gray-400"
          role="status"
        >
          Loading the request…
        </div>

        <div v-else-if="state === 'failed'" role="alert" class="flex gap-3">
          <ExclamationTriangleIcon class="size-6 shrink-0 text-amber-500" aria-hidden="true" />
          <div class="text-sm">
            <p class="font-semibold text-gray-900 dark:text-white">{{ failure.title }}</p>
            <p class="mt-1 text-gray-600 dark:text-gray-300" data-testid="oauth-consent-error">
              {{ failure.message }}
            </p>
            <p class="mt-1 text-gray-600 dark:text-gray-300">
              Start the connection again from the app.
            </p>
            <RouterLink
              :to="{ name: 'Dashboard' }"
              class="mt-4 inline-block font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
              >Go to dashboard</RouterLink
            >
          </div>
        </div>

        <div
          v-else-if="state === 'redirecting'"
          class="text-sm text-gray-600 dark:text-gray-300"
          role="status"
        >
          <template v-if="request?.appRedirect">
            <p>
              Opening the app
              <span class="font-mono font-medium text-gray-900 break-all dark:text-white">{{
                request.appRedirect
              }}</span
              >…
            </p>
            <p v-if="appSlow" class="mt-2" data-testid="app-redirect-fallback">
              If nothing happens, the app may not be installed or your browser may have blocked
              opening it. You can close this tab and start the connection again from the app.
            </p>
          </template>
          <template v-else>Returning to {{ request?.redirectHost ?? 'the app' }}…</template>
        </div>

        <form v-else-if="request" @submit.prevent="allow">
          <div class="flex gap-3">
            <LinkIcon
              class="size-6 shrink-0 text-indigo-600 dark:text-indigo-400"
              aria-hidden="true"
            />
            <div class="min-w-0 text-sm text-gray-600 dark:text-gray-300">
              <p>
                <span
                  class="font-semibold text-gray-900 dark:text-white"
                  data-testid="client-name"
                  >{{ request.clientName ?? 'An unnamed app' }}</span
                >
                wants to access your ManageRunners account.
              </p>
              <p v-if="request.clientHost" class="mt-1" data-testid="client-host">
                Published by
                <span class="font-medium text-gray-900 dark:text-white">{{
                  request.clientHost
                }}</span
                >.
              </p>
              <p v-else class="mt-1">The app named itself; the name is not verified.</p>
              <p v-if="request.appRedirect" class="mt-2">
                After you decide, your browser opens the app
                <span
                  class="font-mono font-medium text-gray-900 break-all dark:text-white"
                  data-testid="redirect-app"
                  >{{ request.appRedirect }}</span
                >.
              </p>
              <p v-else class="mt-2">
                After you decide, you return to
                <span
                  class="font-mono font-medium text-gray-900 break-all dark:text-white"
                  data-testid="redirect-host"
                  >{{ request.redirectHost }}</span
                >.
              </p>
              <p class="mt-2">
                Signed in as
                <span class="font-medium text-gray-900 dark:text-white">{{ auth.username }}</span
                >.
                <button
                  type="button"
                  class="font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 cursor-pointer"
                  @click="switchAccount"
                >
                  Switch account
                </button>
              </p>
            </div>
          </div>

          <div
            v-if="request.appRedirect"
            class="mt-4 flex gap-3 rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-300"
            role="note"
            data-testid="app-redirect-warning"
          >
            <ExclamationTriangleIcon class="size-5 shrink-0" aria-hidden="true" />
            <p>
              This app receives the access through a link that opens an app on your device. Another
              app could claim the same link. Continue only if you just started this connection
              yourself.
            </p>
          </div>

          <div
            v-else-if="request.loopbackRedirect"
            class="mt-4 flex gap-3 rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-300"
            role="note"
            data-testid="loopback-warning"
          >
            <ExclamationTriangleIcon class="size-5 shrink-0" aria-hidden="true" />
            <p>
              This app returns to a program on your own computer. Any program running there could
              receive the access. Continue only if you just started this connection yourself, for
              example from Claude Code.
            </p>
          </div>

          <fieldset class="mt-6">
            <legend class="text-sm font-semibold text-gray-900 dark:text-white">
              Allow the app to
            </legend>
            <div class="mt-3 space-y-3">
              <div v-for="scope in scopes" :key="scope.value" class="flex gap-3">
                <div class="flex h-6 shrink-0 items-center">
                  <input
                    :id="`scope-${scope.value}`"
                    v-model="selected"
                    :value="scope.value"
                    type="checkbox"
                    :aria-describedby="`scope-${scope.value}-description`"
                    class="size-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-600 dark:border-white/10 dark:bg-white/5"
                  />
                </div>
                <div class="text-sm/6">
                  <label
                    :for="`scope-${scope.value}`"
                    class="font-medium text-gray-900 dark:text-white"
                  >
                    {{ scope.label }}
                    <span
                      v-if="scope.mutation"
                      class="ml-1 inline-flex items-center rounded-md bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-800 inset-ring inset-ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-300 dark:inset-ring-amber-400/20"
                      >{{
                        scope.paidResources ? 'Changes runners, costs money' : 'Changes runners'
                      }}</span
                    >
                  </label>
                  <p
                    :id="`scope-${scope.value}-description`"
                    class="text-gray-500 dark:text-gray-400"
                  >
                    {{ scope.description }}
                  </p>
                </div>
              </div>
            </div>
          </fieldset>

          <p class="mt-4 text-xs text-gray-500 dark:text-gray-400">
            You can disconnect the app at any time under Profile → Connected apps.
          </p>

          <div v-if="actionError" class="mt-4" role="alert">
            <p class="text-sm text-red-600 dark:text-red-400">{{ actionError }}</p>
            <div v-if="needsVerification" class="mt-3">
              <EmailVerificationForm @verified="needsVerification = false" />
            </div>
          </div>

          <div class="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              :disabled="busy"
              class="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 disabled:cursor-wait disabled:opacity-50 dark:bg-white/10 dark:text-white dark:inset-ring-white/5 dark:hover:bg-white/20 cursor-pointer"
              @click="deny"
            >
              Deny
            </button>
            <button
              type="submit"
              :disabled="busy || selected.length === 0"
              class="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-indigo-500 dark:hover:bg-indigo-400 cursor-pointer"
            >
              {{ state === 'submitting' ? 'Connecting…' : 'Allow access' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ExclamationTriangleIcon, LinkIcon } from '@heroicons/vue/24/outline';
import {
  approveAuthorizationRequest,
  denyAuthorizationRequest,
  getAuthorizationRequest,
  type OAuthAuthorizationRequest,
} from '@/api/oauth';
import {
  consentErrorMessage,
  defaultSelectedScopes,
  isFollowableRedirect,
  oauthApiError,
} from '@/lib/oauth';
import { PAT_SCOPES } from '@/lib/personal-access-tokens';
import { useAuthStore } from '@/stores/auth';
import EmailVerificationForm from '@/components/EmailVerificationForm.vue';

type State = 'loading' | 'ready' | 'submitting' | 'redirecting' | 'failed';

const APP_FALLBACK_MS = 4000;

const logoUrl = new URL('../assets/logo_runner_small.png', import.meta.url).href;
const route = useRoute();
const router = useRouter();
const auth = useAuthStore();

function queryValue(name: string): string {
  const value = route.query[name];
  return ((Array.isArray(value) ? value[0] : value) ?? '').trim();
}

const requestId = queryValue('request');
const state = ref<State>('loading');
const request = ref<OAuthAuthorizationRequest | null>(null);
const selected = ref<string[]>([]);
const failure = ref({ title: '', message: '' });
const actionError = ref<string | null>(null);
const needsVerification = ref(false);
const appSlow = ref(false);

const busy = computed(() => state.value === 'submitting' || state.value === 'redirecting');
const scopes = computed(() =>
  PAT_SCOPES.filter((scope) => request.value?.requestedScopes.includes(scope.value))
);

function fail(title: string, message: string) {
  failure.value = { title, message };
  state.value = 'failed';
}

onMounted(async () => {
  const error = queryValue('error');
  if (error) {
    fail('This connection request is invalid', consentErrorMessage(error));
    return;
  }
  if (!requestId) {
    fail('This connection request is invalid', 'The link has no request.');
    return;
  }
  try {
    request.value = await getAuthorizationRequest(requestId);
    selected.value = defaultSelectedScopes(request.value.requestedScopes);
    state.value = 'ready';
  } catch (e) {
    const apiError = oauthApiError(e, 'load the request');
    if (apiError.notFound) {
      fail(
        'This connection request has expired',
        'It was already answered, or more than 10 minutes have passed.'
      );
    } else {
      fail('The request could not be loaded', apiError.message);
    }
  }
});

function follow(url: string) {
  if (!isFollowableRedirect(url)) {
    fail('The app could not be reached', 'The app registered an address that cannot be opened.');
    return;
  }
  state.value = 'redirecting';
  window.location.assign(url);
  // An app link leaves this page open, and nothing may happen when no app handles the scheme.
  if (request.value?.appRedirect) window.setTimeout(() => (appSlow.value = true), APP_FALLBACK_MS);
}

async function decide(action: () => Promise<string>, verb: string) {
  state.value = 'submitting';
  actionError.value = null;
  try {
    follow(await action());
  } catch (e) {
    const apiError = oauthApiError(e, verb);
    if (apiError.notFound) {
      fail(
        'This connection request has expired',
        'It was already answered, or more than 10 minutes have passed.'
      );
      return;
    }
    actionError.value = apiError.message;
    needsVerification.value = apiError.emailNotVerified === true;
    state.value = 'ready';
  }
}

function allow() {
  if (selected.value.length === 0) return;
  const chosen = [...selected.value];
  void decide(() => approveAuthorizationRequest(requestId, chosen), 'connect the app');
}

function deny() {
  void decide(() => denyAuthorizationRequest(requestId), 'deny the request');
}

async function switchAccount() {
  auth.logout();
  await router.push({ name: 'Login', query: { redirect: route.fullPath } });
}
</script>
